// =============================================
// PÁGINA: CRIAR AUTOMAÇÃO
// =============================================
// Arquivo: AutomationCreate.tsx
// Descrição: Página para criar novo fluxo de automação
// =============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FlowEditor from '../../components/FlowEditor/FlowEditor';
import { apiService } from '../../services/api';
import './AutomationForm.css';

const AutomationCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [triggerType, setTriggerType] = useState('keyword');
  const [keywords, setKeywords] = useState('');
  const [showMetadataForm, setShowMetadataForm] = useState(true);

  // Nó inicial padrão
  const initialNodes = [
    {
      id: 'start-1',
      type: 'start',
      position: { x: 250, y: 50 },
      data: { label: 'Início' }
    }
  ];

  const handleSave = async (nodes: any[], edges: any[]) => {
    // Validar nome
    if (!flowName.trim()) {
      alert('Por favor, insira um nome para o fluxo');
      setShowMetadataForm(true);
      return;
    }

    setSaving(true);

    try {
      const flowData = {
        nome: flowName,
        descricao: flowDescription,
        ativo: false, // Criar inativo por padrão
        trigger_type: triggerType,
        trigger_config: {
          keywords: keywords.split(',').map(k => k.trim()).filter(k => k)
        },
        flow_data: {
          nodes,
          edges
        }
      };

      const response = await apiService.createAutomationFlow(flowData);

      if (response.success) {
        alert('Fluxo criado com sucesso!');
        navigate('/automacoes');
      } else {
        alert('Erro ao criar fluxo: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      alert('Erro ao salvar fluxo');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (window.confirm('Deseja sair sem salvar?')) {
      navigate('/automacoes');
    }
  };

  return (
    <div className="automation-form-page">
      {/* Header */}
      <div className="form-header">
        <button className="back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="header-info">
          <h1>Criar Novo Fluxo</h1>
          <p>Monte seu fluxo de automação visualmente</p>
        </div>
        <button
          className="metadata-toggle"
          onClick={() => setShowMetadataForm(!showMetadataForm)}
        >
          <i className={`fas fa-${showMetadataForm ? 'eye-slash' : 'eye'}`}></i>
        </button>
      </div>

      {/* Metadata Form */}
      {showMetadataForm && (
        <div className="metadata-panel">
          <div className="metadata-form">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="flowName">
                  Nome do Fluxo <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="flowName"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  placeholder="Ex: Checklist Diário de Veículos"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="flowDescription">Descrição</label>
                <textarea
                  id="flowDescription"
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  placeholder="Descreva o objetivo deste fluxo..."
                  rows={2}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="triggerType">Tipo de Gatilho</label>
                <select
                  id="triggerType"
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value)}
                >
                  <option value="keyword">Palavra-chave</option>
                  <option value="manual">Manual</option>
                  <option value="schedule">Agendado</option>
                </select>
              </div>

              {triggerType === 'keyword' && (
                <div className="form-field">
                  <label htmlFor="keywords">Palavras-chave</label>
                  <input
                    type="text"
                    id="keywords"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="checklist, iniciar, começar (separadas por vírgula)"
                  />
                </div>
              )}
            </div>

            <button
              className="continue-btn"
              onClick={() => setShowMetadataForm(false)}
            >
              Continuar para Editor
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Flow Editor */}
      <div className={`editor-container ${showMetadataForm ? 'hidden' : ''}`}>
        <FlowEditor
          initialNodes={initialNodes}
          initialEdges={[]}
          onSave={handleSave}
        />
      </div>

      {/* Saving overlay */}
      {saving && (
        <div className="saving-overlay">
          <div className="saving-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Salvando fluxo...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationCreate;
